import { useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { track } from "../services/analytics";
import {
  formatAiNextAction,
  formatLeadCategory,
  formatLeadStatus,
  getLeadStatusColor,
} from "../utils/displayFormatters";
import AboutLead from "./AboutLead";
import LeadComposer from "./LeadComposer";
import LeadMessaging from "./LeadMessaging";
import LeadNotes from "./LeadNotes";
import LeadStatusFlow from "./LeadStatusFlow";
import ObjectHistory from "./ObjectHistory";
import ReasonLine from "./ReasonLine";
import SidecartSection from "./SidecartSection";

const ICON = {
  back: "M15 19l-7-7 7-7",
  note: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
  clock: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z",
};

const BIG_BUTTON =
  "flex flex-1 items-center justify-center rounded-lg border px-3 py-3 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2";

const Fact = ({ label, children }) => (
  <div className="min-w-0">
    <dt className="text-[10px] font-semibold uppercase tracking-wider text-text-secondary">{label}</dt>
    <dd className="ph-mask break-words text-sm text-gray-800">{children || "-"}</dd>
  </div>
);

/**
 * The lead as a full-screen page in the Mobile view. Read-first: the owner can
 * call, text, reply and change the lead status. LeadDetail owns the data and
 * the status modals, and passes the handlers in.
 */
const LeadMobilePage = ({
  authToken,
  lead,
  nameOrPhone,
  onStatusChange,
  onLeadUpdate,
  onSent,
  onMessagesRead,
  messageRefreshTrigger,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [statusOpen, setStatusOpen] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);
  const [notesExpanded, setNotesExpanded] = useState(false);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const conversationRef = useRef(null);

  const hasPhone = !!lead.phone;
  const canText = hasPhone && (lead.location?.messaging_enabled ?? false);
  const status = formatLeadStatus(lead.status);
  const nextAction = formatAiNextAction(lead.next_action || lead.ai_next_action);

  // The list keeps its filters in the URL, so one step back restores them. Only
  // step back when the list opened this page (LeadCard sets fromList).
  const goBack = () => (location.state?.fromList ? navigate(-1) : navigate("/leads"));

  const openComposer = () => {
    setComposerOpen(true);
    conversationRef.current?.scrollIntoView?.({ block: "start" });
  };

  return (
    <div className="pb-6">
      <div data-testid="lead-top-bar" className="sticky top-20 z-[60] border-b border-border bg-white">
        <div className="flex items-center gap-2 px-2 py-2">
          <button
            type="button"
            aria-label="Back"
            onClick={goBack}
            className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg text-primary"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={ICON.back} />
            </svg>
          </button>
          <h1 className="ph-mask min-w-0 flex-1 truncate text-base font-semibold text-gray-900">
            {nameOrPhone}
          </h1>
          <button
            type="button"
            aria-label={`Status: ${status.label}`}
            aria-expanded={statusOpen}
            onClick={() => setStatusOpen((open) => !open)}
            className={`flex-shrink-0 rounded-full px-3 py-1 text-xs font-medium whitespace-nowrap ${getLeadStatusColor(lead.status)}`}
          >
            {status.label}
          </button>
        </div>
        {statusOpen && (
          <div className="border-t border-border px-4 py-3">
            <LeadStatusFlow
              currentStatus={lead.status}
              currentCategory={lead.lead_category || "prospect"}
              onStatusChange={(key) => {
                setStatusOpen(false);
                onStatusChange(key);
              }}
              statusOnly
            />
          </div>
        )}
      </div>

      <div className="space-y-3 px-4 pt-3">
        <div className="flex gap-2">
          {hasPhone ? (
            <a
              data-attr="quick-action-call"
              href={`tel:${lead.phone}`}
              onClick={() => track("lead_call_clicked", { lead_id: lead.id })}
              className={`${BIG_BUTTON} border-border bg-white text-gray-700`}
            >
              Call
            </a>
          ) : (
            <span className={`${BIG_BUTTON} border-border text-gray-700 opacity-40`}>Call</span>
          )}
          <button
            type="button"
            data-attr="quick-action-text"
            disabled={!canText}
            onClick={openComposer}
            className={`${BIG_BUTTON} border-primary bg-primary text-white disabled:cursor-not-allowed disabled:opacity-50`}
          >
            Text
          </button>
        </div>

        <section className="overflow-hidden rounded-lg border border-border bg-white">
          {nextAction && (
            <div className="px-4 py-3">
              <h2 className="text-[10px] font-semibold uppercase tracking-wider text-text-secondary">
                Next action
              </h2>
              <p className="text-sm font-medium text-gray-800">{nextAction}</p>
              {lead.ai_follow_up_reason && (
                <p className="ph-mask mt-1 text-xs italic text-gray-500">{lead.ai_follow_up_reason}</p>
              )}
            </div>
          )}
          <ReasonLine lead={lead} onLeadUpdate={onLeadUpdate} embedded />
        </section>

        <AboutLead lead={lead} />

        <section className="rounded-lg border border-border bg-white px-4 py-3">
          <dl data-testid="key-facts" className="grid grid-cols-2 gap-x-4 gap-y-3">
            <Fact label="Category">{formatLeadCategory(lead.lead_category || "prospect").label}</Fact>
            <Fact label="Source">{lead.source}</Fact>
            <Fact label="Location">{lead.location?.name}</Fact>
            <Fact label="Phone">{lead.phone}</Fact>
            <Fact label="Email">{lead.email}</Fact>
          </dl>
        </section>

        <section
          ref={conversationRef}
          data-testid="conversation-section"
          className="scroll-mt-32 rounded-lg border border-border bg-white"
        >
          <div className="flex items-center justify-between px-4 pt-3">
            <h2 className="text-[10px] font-semibold uppercase tracking-wider text-text-secondary">
              Conversation
            </h2>
            {!composerOpen && (
              <button
                type="button"
                onClick={openComposer}
                disabled={!canText}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                Reply
              </button>
            )}
          </div>
          <LeadMessaging
            card
            authToken={authToken}
            leadId={lead.id}
            lead={lead}
            onMessagesRead={onMessagesRead}
            refreshTrigger={messageRefreshTrigger}
          />
          {composerOpen && (
            <div className="px-3 pb-3 pt-2">
              <LeadComposer
                authToken={authToken}
                lead={lead}
                onSent={onSent}
                autoFocus
                surface="conversation_card"
                showTemplateChips
              />
            </div>
          )}
          <div className="pb-3" />
        </section>

        <SidecartSection
          id="notes"
          title="Notes"
          icon={ICON.note}
          count={lead.note_count || 0}
          expanded={notesExpanded}
          onToggle={() => setNotesExpanded(!notesExpanded)}
        >
          <LeadNotes authToken={authToken} id={lead.id} onNoteSelect={() => {}} />
        </SidecartSection>

        <SidecartSection
          id="history"
          title="History"
          icon={ICON.clock}
          expanded={historyExpanded}
          onToggle={() => setHistoryExpanded(!historyExpanded)}
        >
          <ObjectHistory authToken={authToken} objectType="lead" objectId={lead.id} compact />
        </SidecartSection>
      </div>
    </div>
  );
};

export default LeadMobilePage;
